// AI Translate (แปลให้คนไข้) — browser side. Spec: docs/ai-translate-spec.md.
// The clinical-text builder + hash are the SERVER's (functions/lib/aiTranslate.js), imported
// as-is so the panel's "chart changed since translation" check can never disagree with the
// server's regenerate gate.
import { redirectToLogin } from './api.js'
import { buildClinicalText, isClinicalEmpty, sha256Hex, validSummary } from '../../functions/lib/aiTranslate.js'

export { buildClinicalText, isClinicalEmpty, validSummary }

export async function clinicalHash(chart) {
  return sha256Hex(buildClinicalText(chart))
}

/*
 * The model answers `{"patient_summary":"…","staff_notes":"…"}` and the server streams
 * those raw JSON characters. While it is still being written, pull out as much of
 * patient_summary as has arrived, unescaped, so staff watch the Thai appear.
 * staff_notes is never shown mid-stream.
 */
export function partialJsonString(raw, key) {
  const m = new RegExp(`"${key}"\\s*:\\s*"`).exec(raw)
  if (!m) return ''
  let out = ''
  for (let i = m.index + m[0].length; i < raw.length; i++) {
    const c = raw[i]
    if (c === '"') break
    if (c !== '\\') { out += c; continue }
    const n = raw[i + 1]
    if (n === undefined) break // escape split across chunks — wait for the rest
    if (n === 'u') {
      const hex = raw.slice(i + 2, i + 6)
      if (hex.length < 4) break
      out += String.fromCharCode(parseInt(hex, 16))
      i += 5
      continue
    }
    out += { n: '\n', t: '\t', r: '', b: '', f: '' }[n] ?? n
    i += 1
  }
  return out
}

function errorFromBody(status, body) {
  const err = new Error(body?.detail || body?.error || `HTTP ${status}`)
  err.status = status
  err.code = body?.error
  err.body = body
  return err
}

/**
 * POST /api/charts/:id/translate, streamed. `onText(patientSummarySoFar)` fires as text
 * arrives. Resolves with the saved ai_summary; rejects with err.code from the server
 * ('unchanged' | 'edited' | 'no_clinical' | 'translate_failed' | 'save_failed' | …).
 */
export async function streamTranslate({ chartId, patientId, treatmentId, overwriteEdits = false, onText, signal }) {
  const r = await fetch(`/api/charts/${encodeURIComponent(chartId)}/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    signal,
    body: JSON.stringify({ patient_id: patientId, treatment_id: treatmentId, overwrite_edits: overwriteEdits }),
  })
  if (r.status === 401) { redirectToLogin(); return new Promise(() => {}) }
  if (!r.ok || !r.body) {
    const body = await r.json().catch(() => null)
    throw errorFromBody(r.status, body)
  }
  const reader = r.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  let raw = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buf += dec.decode(value, { stream: true })
    let i
    while ((i = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, i).trim()
      buf = buf.slice(i + 1)
      if (!line) continue
      let ev
      try { ev = JSON.parse(line) } catch { continue }
      if (ev.t === 'delta') {
        raw += ev.text
        onText?.(partialJsonString(raw, 'patient_summary'))
      } else if (ev.t === 'done') {
        return ev.ai_summary
      } else if (ev.t === 'error') {
        throw errorFromBody(502, ev)
      }
    }
  }
  throw errorFromBody(502, { error: 'translate_failed', detail: 'การเชื่อมต่อขาดก่อนแปลเสร็จ' })
}

export async function saveSummaryEdit({ chartId, patientId, treatmentId, patientSummary, staffNotes }) {
  const r = await fetch(`/api/charts/${encodeURIComponent(chartId)}/translate`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ patient_id: patientId, treatment_id: treatmentId, patient_summary: patientSummary, staff_notes: staffNotes }),
  })
  if (r.status === 401) { redirectToLogin(); return new Promise(() => {}) }
  const body = await r.json().catch(() => null)
  if (!r.ok) throw errorFromBody(r.status, body)
  return body?.ai_summary
}
